/**
 * Studio Detail Page Route Definition (V2)
 * Route: /studio/$studioId
 *
 * SSR: prefetch studio detail so the component's useQuery(['studio', id]) is a
 * cache hit on first paint. /v2/studios/{id} is optional-auth → the server-side
 * fetch returns global data (info + public reviews); per-user favourite state is CSR.
 * The UI lives in $studioId.lazy.tsx so the fixture/skeleton graph stays
 * out of the entry chunk; only loader/head (needed for SSR) remain here.
 */
import { createFileRoute } from '@tanstack/react-router';
import { StudioDetailTemplatePending } from '@/components/v2/page-template-pending';
import { cacheFirst } from '@/lib/queries/cache-first';
import { studioDetailQuery, type StudioQueryData } from '@/lib/queries/detail';
import { entityHeadMeta } from '@/lib/utils/site-meta';

type StudioDetailResult<T> = { ok: true; data: T } | { ok: false };

const toStudioDetailResult = async <T,>(promise: Promise<T>): Promise<StudioDetailResult<T>> => {
  try {
    const data = await promise;
    return typeof data === 'object' && data !== null && 'ok' in data && data.ok === false
      ? { ok: false }
      : { ok: true, data };
  } catch {
    return { ok: false };
  }
};

export const Route = createFileRoute('/_public/studios/$studioId')({
  // Template-first pending: while this route's component chunk downloads, show the
  // studio page's own shape (centered logo hero → buttons → class cards → sticky
  // CTA, no back header) instead of the full-page RoutePending mascot (audit
  // part 2: a detail click must switch immediately, skeletons included).
  pendingComponent: StudioDetailTemplatePending,
  loader: async ({ context, params, preload }) => {
    // Key + queryFn come from the shared factory (app/lib/queries/detail.ts) so
    // the loader-seeded entry, the detail page's useQuery and the data
    // prefetchers can never drift apart. Behavior is byte-for-byte the inline
    // shape this used to spell out.
    const studioQuery = studioDetailQuery(params.studioId);
    const refreshStudio = () => context.queryClient.ensureQueryData(studioQuery);
    // Preload short-circuit: detail-entry cards fire this loader via
    // <Link preload="viewport"> as they scroll past, and a preload run must
    // never touch the network. Serve the cache when present, else an
    // instantly-resolved cold ({ ok: false }) envelope. The result must
    // SETTLE, never park: preloadRoute keeps the loader lane, its abort
    // controller and match resources alive until the loader settles, so a
    // never-resolving promise would leak a permanently-pending lane for
    // every card scrolled past. The ok:false value is never rendered —
    // commits always re-run this loader (defaultPreloadStaleTime is 0);
    // warming the route chunk is all a preload exists for here.
    // getQueryData is optional-chained for the loader-contract tests, which
    // stub the client with ensureQueryData only; a missing reader simply
    // reads as cold cache.
    if (preload) {
      const cached = context.queryClient.getQueryData?.<StudioQueryData>(studioQuery.queryKey);
      return {
        studioDetailPromise:
          cached === undefined
            ? Promise.resolve<StudioDetailResult<StudioQueryData>>({ ok: false })
            : toStudioDetailResult(Promise.resolve(cached)),
        studioDetail: undefined,
      };
    }
    // Cache-first gate: the shared gate (app/lib/queries/data-plane, through
    // cacheFirst) owns the freshness decision, so this loader and the
    // prefetchers can never disagree about whether the cache entry is usable.
    // A cached studio resolves the envelope immediately and a stale entry is
    // healed in the background (single-flight-deduped with the mounted
    // queries); a cold cache keeps the streamed fetch below, with its existing
    // retry/error paths.
    const { cached, stream } = cacheFirst(context.queryClient, studioQuery.queryKey, refreshStudio);
    const studioDetailPromise =
      cached !== undefined
        ? toStudioDetailResult(Promise.resolve(cached))
        : toStudioDetailResult(stream());
    // Server-only await so head() can read the entity for SSR OG tags (audit
    // UIUX-02). Client navigations skip it — the promise streams into the lazy
    // component's <Await> (EntityDetailLayout skeleton) and head() falls back
    // to the root defaults while the entity is absent (perf audit part 2,
    // Theme 2: the await blocked every client-side navigation).
    if (typeof document !== 'undefined') {
      return { studioDetailPromise, studioDetail: undefined };
    }
    const studioDetail = await studioDetailPromise;
    const studio = studioDetail.ok && studioDetail.data.ok ? studioDetail.data.value : undefined;
    return { studioDetailPromise, studioDetail: studio };
  },
  head: ({ loaderData }) => {
    const studio = loaderData?.studioDetail;
    if (!studio) return {};
    return {
      ...entityHeadMeta({
        title: `${studio.name} - Studio | TIL`,
        description: studio.description || `See classes and instructors at ${studio.name} on TIL.`,
        image: studio.coverImageUrl || studio.logoUrl,
        path: `/studios/${studio.slug}`,
      }),
    };
  },
});
